<template>
  <v-app class="dev-layout">
    <!-- Slim developer top bar (intentionally distinct from the normal console) -->
    <v-app-bar app color="#141414" dark elevation="0" height="64" class="dev-navbar">
      <div class="dev-navbar__brand" @click="goHome">
        <v-icon color="#38bdf8" size="22" class="mr-2">mdi-code-tags</v-icon>
        <span class="dev-navbar__title">Developer Console</span>
        <span class="dev-navbar__badge">RESTRICTED</span>
      </div>

      <v-spacer></v-spacer>

      <div v-if="identity" class="dev-navbar__identity">
        <v-icon size="16" color="var(--app-text-muted)" class="mr-1">mdi-shield-account-outline</v-icon>
        <span>{{ identity.name || "Developer" }}</span>
      </div>

      <button class="dev-navbar__logout" @click="logout">
        <v-icon size="16" class="mr-1">mdi-logout</v-icon>
        Logout
      </button>
    </v-app-bar>

    <v-main class="dev-main">
      <!-- See LayoutConsoleV2: the wrapper takes the slack so the footer sits on
           main's bottom edge rather than leaving bare main below it. -->
      <div class="dev-main__content">
        <AppBreadcrumb :root="breadcrumbRoot" />
        <router-view />
      </div>

      <AppFooter />
    </v-main>
  </v-app>
</template>

<script>
import { mapState } from "vuex";
import AppBreadcrumb from "@/components/layouts/AppBreadcrumb.vue";

export default {
  components: {
    AppBreadcrumb,
  },
  data: () => ({
    breadcrumbRoot: {
      label: "Developer Console",
      to: { name: "PageDeveloperSettings" },
    },
  }),
  computed: {
    ...mapState({
      devAuth: (state) => state.devAuth.data,
    }),
    identity() {
      return this.devAuth && this.devAuth.identity ? this.devAuth.identity : null;
    },
  },
  watch: {
    // The dev session can be torn down from anywhere — most importantly by
    // api.js when a request fails with an expired/invalid dev token. The moment
    // it's cleared, log the user straight out of the developer console instead
    // of leaving them on a page that can only show backend auth errors.
    devAuth(val) {
      if (!val && this.$route.name !== "PageDeveloperLogin") {
        this.$router.push({ name: "PageDeveloperLogin" }).catch(() => {});
      }
    },
  },
  methods: {
    goHome() {
      if (this.$route.name !== "PageDeveloperSettings") {
        this.$router.push({ name: "PageDeveloperSettings" }).catch(() => {});
      }
    },
    logout() {
      this.$store.dispatch("devLogout");
      this.$router.push({ name: "PageDeveloperLogin" }).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .dev-navbar__identity {
  color: var(--app-text-faint);
}

.dark-mode .dev-main {
  background-color: var(--app-page);
}

/* >>> end auto-dark <<< */

/* Column layout inside Vuetify's own .v-main__wrap so the footer lands after
   the page content and on main's bottom edge. ::v-deep because the wrap is
   rendered by v-main, outside this component's scope id. */
.dev-main ::v-deep .v-main__wrap {
  display: flex;
  flex-direction: column;
}

/* Column for the same reason as .layout-main__content in LayoutConsoleV2: a
   page that wants to fill the viewport says `flex: 1 0 auto` rather than
   guessing `100vh - chrome` and overflowing by the breadcrumb + footer. */
.dev-main__content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}

.dev-layout {
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
}

.dev-navbar {
  border-bottom: 1px solid #2a2a2a !important;
}

.dev-navbar__brand {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.dev-navbar__title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: #e8e8e8;
}

.dev-navbar__badge {
  margin-left: 10px;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(248, 113, 113, 0.16);
  color: #fca5a5;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 1.5px;
}

.dev-navbar__identity {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
  margin-right: 18px;
}

.dev-navbar__logout {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 9px;
  background: rgba(248, 113, 113, 0.12);
  color: #fca5a5;
  font-size: 0.78rem;
  font-weight: 700;
  transition: all 0.2s ease;
}

.dev-navbar__logout:hover {
  background: rgba(248, 113, 113, 0.22);
  color: #fecaca;
}

.dev-main {
  background-color: #d8dce2;
}
</style>
